// The order a pro gives their service categories, as the Services page shows
// it. This mirrors server/utils/serviceCategoryOrder.js, which sorts the
// booking page the same way; change both together.
//
// Categories are free text typed on each service, so "Nails", " nails" and
// "NAILS" are one category: names are matched case-insensitively after
// trimming. A blank category shows as "No category" and always comes last, and
// a service literally called "No category" joins that group.

export const NO_CATEGORY_LABEL = "No category";

const NO_CATEGORY_KEY = "";
const NO_CATEGORY_NAME_KEY = "no category";

export const categoryKey = (value) => {
  if (typeof value !== "string") return NO_CATEGORY_KEY;
  const key = value.trim().normalize("NFC").toLowerCase();
  return key === NO_CATEGORY_NAME_KEY ? NO_CATEGORY_KEY : key;
};

// An empty list is the same as never having set one.
export const hasSavedCategoryOrder = (order) => Array.isArray(order) && order.length > 0;

// The spelling most of a category's services use; the first one seen on a tie.
const mostUsedSpelling = (spellings) => {
  let best = "";
  let bestCount = 0;
  spellings.forEach((count, spelling) => {
    if (count > bestCount) {
      best = spelling;
      bestCount = count;
    }
  });
  return best;
};

/**
 * Groups services by category: the saved categories in saved order, then every
 * other category A to Z, then "No category". Inside a group the services keep
 * the order they came in. With nothing saved this is the A to Z grouping the
 * page has always shown.
 */
export const groupServicesByCategory = (services = [], savedOrder = []) => {
  const buckets = new Map();
  services.forEach((service) => {
    const key = categoryKey(service?.category);
    if (!buckets.has(key)) buckets.set(key, { key, services: [], spellings: new Map() });
    const bucket = buckets.get(key);
    bucket.services.push(service);
    if (key) {
      const spelling = service.category.trim();
      bucket.spellings.set(spelling, (bucket.spellings.get(spelling) || 0) + 1);
    }
  });

  const savedIndex = new Map();
  (Array.isArray(savedOrder) ? savedOrder : []).forEach((entry) => {
    const key = categoryKey(entry);
    if (key && !savedIndex.has(key)) savedIndex.set(key, savedIndex.size);
  });

  return Array.from(buckets.values())
    .map(({ key, services: groupServices, spellings }) => ({
      key,
      label: key ? mostUsedSpelling(spellings) : NO_CATEGORY_LABEL,
      services: groupServices,
    }))
    .sort((a, b) => {
      if (!a.key) return 1;
      if (!b.key) return -1;
      const aSaved = savedIndex.get(a.key);
      const bSaved = savedIndex.get(b.key);
      if (aSaved !== undefined && bSaved !== undefined) return aSaved - bSaved;
      if (aSaved !== undefined) return -1;
      if (bSaved !== undefined) return 1;
      return a.label.localeCompare(b.label);
    });
};

// One flat list in category order, as the booking page lists it once an order
// is saved. Without one the list is returned as it is.
export const sortServicesByCategoryOrder = (services = [], savedOrder = []) =>
  hasSavedCategoryOrder(savedOrder)
    ? groupServicesByCategory(services, savedOrder).flatMap((group) => group.services)
    : services;

// The saved order as the server put it on the list, for when the order itself
// couldn't be read: each listed service carries its category's position once
// one is saved.
export const categoryOrderFromPositions = (services = []) => {
  const positions = new Map();
  services.forEach((service) => {
    const key = categoryKey(service?.category);
    if (key && Number.isFinite(service?.categoryPosition) && !positions.has(key)) {
      positions.set(key, { position: service.categoryPosition, name: service.category.trim() });
    }
  });
  return Array.from(positions.values())
    .sort((a, b) => a.position - b.position)
    .map((entry) => entry.name);
};

/**
 * A category is renamed one service at a time. When the last service leaves a
 * saved category, the name it moves to takes the old one's place, unless that
 * name already has its own place and services in it. Returns the new saved
 * order, or null when nothing should change. Same rule as the server's.
 */
export const planCategoryRename = (savedOrder, { from, to, otherCategories = [] } = {}) => {
  if (!hasSavedCategoryOrder(savedOrder)) return null;
  const fromKey = categoryKey(from);
  const toKey = categoryKey(to);
  if (!fromKey || !toKey || fromKey === toKey) return null;
  const others = new Set(otherCategories.map(categoryKey));
  if (others.has(fromKey)) return null;
  const index = savedOrder.findIndex((entry) => categoryKey(entry) === fromKey);
  if (index === -1) return null;
  const toIsPlaced = savedOrder.some((entry) => categoryKey(entry) === toKey);
  if (toIsPlaced && others.has(toKey)) return null;
  const name = to.trim();
  return savedOrder.flatMap((entry, position) => {
    if (position === index) return [name];
    return categoryKey(entry) === toKey ? [] : [entry];
  });
};

// The booking page's own heading for services without a real category.
export const BOOKING_PAGE_OTHER_LABEL = "Other services";
// Headings only help once there is more than one category to tell apart, and
// the row of jump links only once the menu is long enough to need it.
export const MIN_CATEGORIES_FOR_HEADINGS = 2;
export const MIN_CATEGORIES_FOR_JUMP_LINKS = 5;

// "Other" is the category a service gets when the pro never typed one, so on
// the booking page it joins the services with none rather than sitting next
// to them under a heading of its own.
const isOtherOnBookingPage = (key) => !key || key === "other";

/**
 * The booking page's category groups, when the pro shows categories there:
 * the saved order (as the server marked it on each service), otherwise A to Z,
 * the same as the Services page. "Other", blank and missing categories make one
 * last group, "Other services", which never counts toward the thresholds.
 * Inside a group the services keep the order the list came in.
 */
export const bookingPageCategoryGroups = (services = []) => {
  const grouped = groupServicesByCategory(services, categoryOrderFromPositions(services));
  const named = grouped.filter((group) => !isOtherOnBookingPage(group.key));
  // Keep the incoming order inside "Other services" too, across both sources.
  const otherServices = services.filter((service) =>
    isOtherOnBookingPage(categoryKey(service?.category)),
  );
  const groups = otherServices.length
    ? [...named, { key: "", label: BOOKING_PAGE_OTHER_LABEL, services: otherServices }]
    : named;
  return {
    groups,
    showHeadings: named.length >= MIN_CATEGORIES_FOR_HEADINGS,
    showJumpLinks: named.length >= MIN_CATEGORIES_FOR_JUMP_LINKS,
  };
};
